import React, { useCallback, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from 'api/api'
import styles from 'components/answers/vote-control.module.scss'
import cn from 'classnames'
import ReplyButton from 'components/shared/ReplyButton'
import { cacheAnswer } from 'state/cache'
import type { AnswerOrDraft } from 'types/answers'

export default function VoteControl({
    answer,
    isUserSignedIn,
    onReplyClick = undefined,
    quoteActive = false,
    // The reply lands in the composer on screen: keep the keyboard's focus.
    replyPreservesFocus = false,
    highlight = false,
    childCount = undefined,
    className = undefined,
    theme = undefined,
}) {
    const queryClient = useQueryClient()

    const upvoteMutation = useMutation({
        mutationFn: () => api.upvoteAnswer(answer.id),
        onMutate: async () => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ['answer', answer.id] })

            // Snapshot the previous value
            const previousAnswer = queryClient.getQueryData<
                AnswerOrDraft & { vote_total?: number }
            >(['answer', answer.id])

            // Optimistically update the cache
            const nextAnswer = previousAnswer
                ? {
                      ...previousAnswer,
                      vote_total: (previousAnswer.vote_total || 0) + 1,
                  }
                : null
            if (nextAnswer) cacheAnswer(nextAnswer, false)

            // Return context with the previous value for rollback
            return { previousAnswer }
        },
        onError: (_err, _vars, context) => {
            // Rollback to the previous value on error
            if (context?.previousAnswer) {
                cacheAnswer(context.previousAnswer, false)
            }
        },
        // No need for onSettled/invalidate - the API response already calls cacheAnswer
    })

    const handleVote = useCallback(
        (ev: React.MouseEvent) => {
            ev.stopPropagation()
            ev.preventDefault()

            if (isUserSignedIn) {
                upvoteMutation.mutate()
            }
        },
        [isUserSignedIn, upvoteMutation],
    )

    // Read vote_total directly from the answer prop (which comes from cache)
    const voteTotal = answer?.vote_total || 0

    return (
        <div className={cn(styles.voteControl, className, theme === 'play' && styles.playTheme)}>
            <VoteAmount voteTotal={voteTotal} />
            <div className={styles.rightControls}>
                <VoteButton addVoteTotal={handleVote} />
                {onReplyClick && (
                    <ReplyButton
                        onClick={onReplyClick}
                        quoteActive={quoteActive}
                        preserveFocus={replyPreservesFocus}
                        answer={answer}
                        highlight={highlight}
                        childCount={childCount}
                    />
                )}
            </div>
        </div>
    )
}

function VoteButton({ addVoteTotal }) {
    const voteButtonRef = useRef(null)

    const animateVote = (ref, scale = 1.5) => {
        const duration = 100
        const comp = ref.current
        comp.style.transition = `all ${duration / 1000}s ease-in-out`
        //comp.style.backgroundColor = '#ffab46'
        //comp.style.color = 'white'
        comp.style.transform = `scale(${scale})`
        new Promise(resolve => setTimeout(resolve, duration))
            .then(() => {
                comp.style.backgroundColor = ''
                comp.style.color = ''
                comp.style.transform = ''
                return new Promise(resolve => setTimeout(resolve, duration))
            })
            .then(() => {
                comp.style.transition = ''
            })
    }

    return (
        <div
            className={styles.voteButton}
            data-answer-row-control
            onClick={useCallback(
                ev => {
                    addVoteTotal(ev)
                    animateVote(voteButtonRef)
                },
                [addVoteTotal],
            )}
            ref={voteButtonRef}
        >
            <i className="fa fa-caret-up" />
        </div>
    )
}

function VoteAmount({ voteTotal }) {
    return (
        <div
            className={cn(
                styles.voteAmount,
                voteTotal > 0 && styles.voteAmountBar,
                voteTotal === 0 && styles.emptyVoteAmountBar,
            )}
        >
            <i className="fa fa-caret-up" />
            {voteTotal}
        </div>
    )
}
